import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// Read-only OHLC feed for the proprietary Orion chart. Price bars only; no
// engine state, filters, or policy values are exposed.
export type Candle = { t: number; o: number; h: number; l: number; c: number };

const TF_STEP = { M5: 300, M15: 900, H1: 3600, H4: 14400, D1: 86400, W1: 604800 } as const;
// D1 uses the 22:00 UTC trading-day boundary so the Sunday reopen joins Monday.
// W1 opens at the Sunday 22:00 UTC reopen.
const TF_OFFSET = { M5: 0, M15: 0, H1: 0, H4: 0, D1: 7200, W1: 266400 } as const;
const BARS = { M5: 120, M15: 120, H1: 120, H4: 120, D1: 60, W1: 20 } as const;
const PAGE = 1000;

// Short in-memory cache: higher timeframes read many pages of M5 bars.
const cache = new Map<string, { at: number; value: { candles: Candle[]; dayOpen: number | null } }>();

export const getTerminalCandles = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ tf: z.enum(["M5", "M15", "H1", "H4", "D1", "W1"]) }).parse(d))
  .handler(async ({ data }) => {
    const hit = cache.get(data.tf);
    if (hit && Date.now() - hit.at < 45_000) return hit.value;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const step = TF_STEP[data.tf];
    const off = TF_OFFSET[data.tf];
    const bars = BARS[data.tf];
    // Trading weeks have about 5/7 of calendar M5 bars; read with headroom.
    const need = Math.ceil(((bars + 2) * step) / 300 * (5 / 7) * 1.15);
    const pages = Math.max(1, Math.ceil(need / PAGE));
    const results = await Promise.all(
      Array.from({ length: pages }, (_, i) =>
        supabaseAdmin
          .from("otas_m5_bars")
          .select("t, o, h, l, c")
          .order("t", { ascending: false })
          .range(i * PAGE, i * PAGE + PAGE - 1),
      ),
    );
    if (results.some((r) => r.error)) throw new Error("Candle read failed");
    const asc = results
      .flatMap((r) => r.data ?? [])
      .map((r) => ({ t: Number(r.t), o: Number(r.o), h: Number(r.h), l: Number(r.l), c: Number(r.c) }))
      .sort((a, b) => a.t - b.t);
    const out: Candle[] = [];
    for (const b of asc) {
      const k = Math.floor((b.t + off) / step) * step - off;
      const last = out[out.length - 1];
      if (last && last.t === k) {
        last.h = Math.max(last.h, b.h); last.l = Math.min(last.l, b.l); last.c = b.c;
      } else out.push({ t: k, o: b.o, h: b.h, l: b.l, c: b.c });
    }
    const lastT = asc.at(-1)?.t ?? 0;
    const dayStart = Math.floor(lastT / 86400) * 86400;
    const dayOpen = asc.find((b) => b.t >= dayStart)?.o ?? asc[0]?.o ?? null;
    // Drop a partial first bucket so the leftmost candle is not truncated.
    const trimmed = out.length > bars ? out.slice(-bars) : out.slice(1);
    const value = { candles: trimmed, dayOpen };
    cache.set(data.tf, { at: Date.now(), value });
    return value;
  });
